import { DateTime } from 'luxon';
import { ATTENDANCE_MODES, EVENT_GROUP_TYPES, QR_TYPES } from '../constants/schedule.constants';
import { toCheckInWindowMinutes } from './check-in-window';

// Read a UTC ISO string back as { hour, minute } in the org timezone.
const toTimeOfDay = (iso, timeZone) => {
  const dt = timeZone ? DateTime.fromISO(iso, { zone: timeZone }) : DateTime.fromISO(iso);
  return { hour: dt.hour, minute: dt.minute };
};

const toDateOnly = (iso, timeZone) => {
  const dt = timeZone ? DateTime.fromISO(iso, { zone: timeZone }) : DateTime.fromISO(iso);
  return dt.toFormat('yyyy-LL-dd');
};

// POST /schedules/event-groups body for a recurring educational event. Series
// end is a separate date-only field (recurrenceEndDate), not derived from endTime.
export function buildEducationalEventGroupBody(values, timeZone) {
  const usesLink = values.attendanceMode !== ATTENDANCE_MODES.IN_PERSON;
  return {
    type: EVENT_GROUP_TYPES.EDUCATIONAL,
    name: (values.title || '').trim(),
    details: {
      startTime: toTimeOfDay(values.startTime, timeZone),
      endTime: toTimeOfDay(values.endTime, timeZone),
      isAllDay: false,
      location: values.location || '',
      speaker: values.speaker || '',
      hostIds: values.hostIds,
      attendanceMode: values.attendanceMode,
      webLink: usesLink ? (values.webLink || '').trim() : '',
      allowSelfAttestation: Boolean(values.allowSelfAttestation),
      qrType: values.qrType || QR_TYPES.ROTATING,
      ...toCheckInWindowMinutes(values),
      knowledgeItems: (values.knowledgeItems || []).map(({ id, type, url, title }) => ({
        id,
        type,
        url,
        title,
      })),
      recurrence: {
        cadence: values.cadence,
        startDate: toDateOnly(values.startTime, timeZone),
        endDate: values.recurrenceEndDate,
      },
    },
  };
}

// Reshape a single-event PATCH diff into a group PATCH body — only the changed
// fields, in the group's details shape. The caller adds recurrence when changed.
export function buildEducationalEventGroupPatch(patch, timeZone) {
  const details = {};
  if (patch.startTime !== undefined) details.startTime = toTimeOfDay(patch.startTime, timeZone);
  if (patch.endTime !== undefined) details.endTime = toTimeOfDay(patch.endTime, timeZone);
  if (patch.location !== undefined) details.location = patch.location;

  const attributes = patch.attributes || {};
  if (attributes.hostIds !== undefined) details.hostIds = attributes.hostIds;
  if (attributes.speaker !== undefined) details.speaker = attributes.speaker;
  if (attributes.attendanceMode !== undefined) details.attendanceMode = attributes.attendanceMode;
  if (attributes.webLink !== undefined) details.webLink = attributes.webLink;
  if (attributes.allowSelfAttestation !== undefined) {
    details.allowSelfAttestation = attributes.allowSelfAttestation;
  }
  if (attributes.qrType !== undefined) details.qrType = attributes.qrType;
  if (attributes.checkInOpensBeforeStartMinutes !== undefined) {
    details.checkInOpensBeforeStartMinutes = attributes.checkInOpensBeforeStartMinutes;
  }
  if (attributes.checkInClosesAfterEndMinutes !== undefined) {
    details.checkInClosesAfterEndMinutes = attributes.checkInClosesAfterEndMinutes;
  }

  const body = {};
  if (patch.title !== undefined) body.name = patch.title;
  if (Object.keys(details).length > 0) body.details = details;
  return body;
}
